import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Users } from "lucide-react";
import { createTenantPeopleSource } from "@playbookai/api-clients";
import {
  Skeleton,
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  EmptyState,
  FormField,
  Input,
  PeopleField,
  Select,
  SettingsGroup,
  type PeopleFieldValue,
} from "@playbookai/ui";
import {
  AdminAboutCard,
  AdminRelatedCard,
  AdminSection,
} from "./AdminSection.js";
import {
  addDirectoryGroupMember,
  createDirectoryGroup,
  listDirectoryGroupMembers,
  listAllDirectoryGroups,
  removeDirectoryGroupMember,
  type DirectoryGroupMember,
} from "../../api/directory-group-membership-api.js";
import { AdminApiError, listAdminMembers } from "../../api/admin-users-api.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { ADMIN_ROUTE, ADMIN_SECTION_ROUTES } from "../../routes.js";

/**
 * Manual directory-group membership admin page — TEMPORARY.
 *
 * Stakeholder-demo bridge, ahead of PB-63 (Platform People & Organization
 * Directory sync/UI). Lets a tenant admin add/remove a tenant member from a
 * directory group by hand, plus a bare-bones create-a-group form for
 * environments with no groups yet (no Entra/SCIM sync configured). Reads and
 * writes the same `directory_groups` / `directory_group_members` tables the
 * LAMA campaign scope step already resolves through, so a change here is
 * immediately visible there.
 *
 * Delete this file, its route in App.tsx, its link on IdentityAdmin, the
 * route constant in routes.ts, and `api/directory-group-membership-api.ts`
 * once PB-63 lands with a real sync/UI. The backend module it calls
 * (`services/auth/src/directory-group-membership/`) is likewise scaffolding.
 *
 * Admin-only: it lives under the `/admin` tree, gated once by
 * `RequireTenantAdmin` in App.tsx; the server remains the authoritative gate.
 */

/** Every cached group list for this tenant, whatever search narrowed it. The
 *  picker's labels carry `memberCount`, so a membership write staleness-affects
 *  all of them, not just the variant currently on screen. */
const groupsKeyPrefix = (tenantId: string | null) =>
  ["auth", "admin", "directory-group-membership", tenantId, "groups"] as const;
/* Derived from the prefix rather than spelled out again: two copies of one key
   shape is how an invalidation silently stops matching its query. */
const groupsKey = (tenantId: string | null, query: string) =>
  [...groupsKeyPrefix(tenantId), query] as const;
const membersKey = (tenantId: string | null, groupId: string | null) =>
  [
    "auth",
    "admin",
    "directory-group-membership",
    tenantId,
    "members",
    groupId,
  ] as const;

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof AdminApiError ? cause.message : fallback;
}

export function DirectoryGroupMembershipAdmin(): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const claims = useAuthStore((state) => state.claims);
  const tenantId = claims?.tenantId ?? null;

  const [groupId, setGroupId] = useState("");
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [groupQuery, setGroupQuery] = useState("");
  const [debouncedGroupQuery, setDebouncedGroupQuery] = useState("");
  // Removing someone changes the audience of any live campaign scoped to this
  // group, so it confirms first — mirroring `MembersAdmin.tsx`'s Remove Modal
  // (PB-14's "the only irreversible control in the table confirms first" rule).
  /* The GROUP is snapshotted alongside the member, not read from `groupId` at
     confirm time. The mutation used the live selection, so switching group with
     the dialog open removed the person from whichever group was selected THEN —
     a destructive write against a group the admin never confirmed. Same stale-
     selection shape this page already fixed once for the member panel. */
  const [removeTarget, setRemoveTarget] = useState<{
    readonly member: DirectoryGroupMember;
    readonly groupId: string;
  } | null>(null);
  const [candidate, setCandidate] = useState<PeopleFieldValue>(null);
  const candidateUserId = candidate?.kind === "person" ? candidate.id : "";

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedGroupQuery(groupQuery.trim()),
      300,
    );
    return () => clearTimeout(timer);
  }, [groupQuery]);

  const groups = useQuery({
    queryKey: groupsKey(tenantId, debouncedGroupQuery),
    // EVERY page. A single page silently hid the remainder, so a tenant with
    // more groups than the server page size had groups an admin could not pick.
    queryFn: () => listAllDirectoryGroups({ query: debouncedGroupQuery }),
  });
  const members = useQuery({
    queryKey: membersKey(tenantId, groupId || null),
    queryFn: () => listDirectoryGroupMembers(groupId),
    enabled: groupId !== "",
  });
  // Reuses the existing member-search read (MembersAdmin's own picker) rather
  // than adding a second one: the person to add is always an existing tenant
  // member, never a directory user who has not joined yet.
  /* A new source per tenant, per group AND per member list: the field caches
     a search per source for 60 s, a session swap must not serve one tenant's
     answers to the next (PB-1556), and an Add or Remove changes who may be
     added, so the old answers must not offer someone who just joined. */
  const candidateSource = useMemo(
    () =>
      createTenantPeopleSource({
        search: async (query, cursor, signal) => {
          const page = await listAdminMembers(
            cursor,
            { query, status: "active", excludeDirectoryGroupId: groupId },
            signal,
          );
          return {
            items: page.users.map((user) => ({
              id: user.userId,
              name: user.displayName ?? user.email ?? user.userId,
              email: user.email,
            })),
            nextCursor: page.nextCursor,
          };
        },
      }),
    // `tenantId` and `members.data` are triggers, not inputs.
    [tenantId, groupId, members.data],
  );

  /* Takes the group explicitly: a removal confirmed against a snapshot must
     refresh THAT group's members, not whatever is selected when it lands. */
  const refreshMembers = async (
    forGroupId: string = groupId,
  ): Promise<void> => {
    /* The group list too, not only the member list: `groupOptions` renders each
       group's `memberCount`, so after an Add or Remove the picker keeps showing
       the pre-write count until some later reload happens to refetch it. Keyed
       on the PREFIX so every search variant is invalidated, not just the one
       the current query built. */
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: membersKey(tenantId, forGroupId || null),
      }),
      queryClient.invalidateQueries({ queryKey: groupsKeyPrefix(tenantId) }),
    ]);
  };

  const addMember = useMutation({
    mutationFn: (userId: string) => addDirectoryGroupMember(groupId, userId),
    onSuccess: async () => {
      setCandidate(null);
      await refreshMembers();
    },
  });
  const removeMember = useMutation({
    mutationFn: ({
      groupId: fromGroupId,
      userId,
    }: {
      readonly groupId: string;
      readonly userId: string;
    }) => removeDirectoryGroupMember(fromGroupId, userId),
    onSuccess: (_result, { groupId: fromGroupId }) =>
      refreshMembers(fromGroupId),
  });
  const createGroup = useMutation({
    mutationFn: (displayName: string) => createDirectoryGroup(displayName),
    onSuccess: async (created) => {
      setNewGroupName("");
      // The group below is about to change, so the pending Person selection is
      // no longer the one the picker is showing — see the picker's onChange.
      setCandidate(null);
      // Clear any in-flight search first so the refetch below is unfiltered
      // and is guaranteed to include the brand-new group.
      if (groupQuery !== "") setGroupQuery("");
      if (debouncedGroupQuery !== "") setDebouncedGroupQuery("");
      await queryClient.invalidateQueries({
        queryKey: groupsKey(tenantId, ""),
      });
      // Select the newly-created group exactly the way the picker's own
      // onChange does — both the id and the pinned display name.
      setSelectedGroupName(created.displayName);
      setGroupId(created.groupId);
    },
  });

  const groupOptions = (groups.data?.groups ?? []).map((group) => ({
    value: group.groupId,
    label: `${group.displayName} (${group.memberCount})`,
  }));
  /* `listAllDirectoryGroups` stops at its 20-page cap and REPORTS the leftover
     cursor rather than truncating silently, precisely so a caller can say the
     list is partial. Discarding it put the picker back where it started: an
     admin sees a complete-looking list with groups missing from it. */
  const groupsIncomplete = (groups.data?.nextCursor ?? null) !== null;

  // A failed person search is the field's own, with its own Retry.
  const loadError = groups.error
    ? errorMessage(groups.error, "Could not load directory groups.")
    : members.error
      ? errorMessage(members.error, "Could not load this group's members.")
      : null;
  const actionError =
    (addMember.error ?? removeMember.error ?? createGroup.error)
      ? errorMessage(
          addMember.error ?? removeMember.error ?? createGroup.error,
          "That change could not be applied.",
        )
      : null;

  return (
    <AdminSection
      icon={Users}
      title="Group membership"
      description="Add or remove a tenant member from an existing directory group by hand."
      /* This page has no entry in the admin rail — reachable only from
         Identity & directory, same as its sibling Directory group roles. */
      breadcrumb={
        <Button
          variant="ghost"
          className="-ms-2 h-8 gap-1.5 self-start whitespace-nowrap px-2 text-pb-text-muted hover:text-pb-text"
          onClick={() =>
            navigate(ADMIN_SECTION_ROUTES["identity"] ?? ADMIN_ROUTE)
          }
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Identity &amp; directory
        </Button>
      }
      meta={
        <>
          <AdminAboutCard title="About this page">
            A manual bridge ahead of full directory sync. It edits the same
            groups LAMA campaign scope already reads, so a change here shows up
            there immediately.
          </AdminAboutCard>
          <AdminRelatedCard ids={["members"]} />
        </>
      }
    >
      <Alert tone="info" title="Temporary">
        A manual bridge ahead of full directory sync. It edits the same groups
        LAMA campaign scope already reads, so a change here shows up there
        immediately.
      </Alert>

      {/* "Something went wrong" named nothing and covered two different
          events. A failed READ can be retried; a failed ACTION cannot be
          retried by this button, so they are told apart. */}
      {loadError ? (
        <Alert tone="critical" title="Couldn't load this page">
          {loadError}
          <Button
            variant="secondary"
            onClick={() => {
              /* Mirror each query's own `enabled`. `refetch()` runs a DISABLED
                 query in TanStack v5, so retrying with no group chosen asked
                 for /directory-groups//members. */
              void groups.refetch();
              if (groupId !== "") void members.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      ) : actionError ? (
        <Alert tone="critical" title="That did not work">
          {actionError}
        </Alert>
      ) : null}

      {groupsIncomplete ? (
        <Alert tone="watch" title="Not every group is listed">
          This tenant has more directory groups than this page walks. Search by
          name to reach one that is not in the list below.
        </Alert>
      ) : null}

      <SettingsGroup
        title="Group"
        description="Pick an existing group, or create a new one for this demo."
      >
        <Card>
          <CardBody className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-2">
              <Input
                label="Find a group"
                placeholder="Search by name"
                value={groupQuery}
                onChange={(event) => setGroupQuery(event.target.value)}
              />
              <Select
                label="Directory group"
                placeholder={groups.isPending ? "Loading…" : "Choose a group"}
                options={groupOptions}
                value={groupId}
                disabled={groups.isPending}
                onChange={(value) => {
                  // Drop any pending Person selection: it was chosen against
                  // the OUTGOING group's candidate list (which excludes that
                  // group's existing members), so carrying it over would let
                  // Add post someone the picker is no longer offering.
                  setCandidate(null);
                  // Pin the display name at selection time, from the CURRENT
                  // groups.data — not re-derived later, since a subsequent
                  // search can narrow this group out of groupOptions while it
                  // is still the one the Members panel below is acting on.
                  const chosen = groups.data?.groups.find(
                    (candidate) => candidate.groupId === value,
                  );
                  setSelectedGroupName(chosen?.displayName ?? "");
                  setGroupId(value);
                }}
              />
            </div>

            <div className="flex flex-wrap items-end gap-2 border-t border-pb-border/70 pt-4">
              <Input
                label="New group name"
                placeholder="e.g. Demo Reviewers"
                value={newGroupName}
                onChange={(event) => setNewGroupName(event.target.value)}
              />
              <Button
                variant="primary"
                disabled={newGroupName.trim() === "" || createGroup.isPending}
                onClick={() => createGroup.mutate(newGroupName.trim())}
              >
                Create
              </Button>
            </div>
          </CardBody>
        </Card>
      </SettingsGroup>

      {groupId ? (
        <SettingsGroup
          title={
            selectedGroupName ? `Members of ${selectedGroupName}` : "Members"
          }
          description="Everyone currently in this group."
        >
          <Card>
            <CardBody className="flex flex-col gap-4">
              {members.isPending ? (
                <Skeleton className="h-20 w-full" />
              ) : members.data && members.data.members.length === 0 ? (
                <EmptyState
                  variant="no-results"
                  title="No members yet"
                  description="Add someone below."
                />
              ) : (
                <ul className="flex flex-col gap-2">
                  {(members.data?.members ?? []).map((member) => (
                    <li
                      key={member.userId}
                      className="flex flex-wrap items-center justify-between gap-3 border-b border-pb-border/70 pb-2 last:border-0"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {member.displayName ?? member.email ?? member.userId}
                        </p>
                        {member.email ? (
                          <p className="text-xs text-pb-text-muted">
                            {member.email}
                          </p>
                        ) : null}
                      </div>
                      {member.source === "admin" ? (
                        <Button
                          variant="ghost"
                          disabled={
                            removeMember.isPending &&
                            removeMember.variables?.userId === member.userId
                          }
                          onClick={() => setRemoveTarget({ member, groupId })}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              {members.data?.truncated ? (
                <Badge>Showing a partial list</Badge>
              ) : null}

              <div className="flex flex-wrap items-end gap-2 border-t border-pb-border/70 pt-4">
                <FormField label="Person" className="w-auto">
                  <PeopleField
                    label="Person"
                    emptyLabel="Choose a person"
                    source={candidateSource}
                    value={candidate}
                    onChange={setCandidate}
                  />
                </FormField>
                <Button
                  variant="primary"
                  disabled={candidateUserId === "" || addMember.isPending}
                  onClick={() => addMember.mutate(candidateUserId)}
                >
                  Add
                </Button>
              </div>
            </CardBody>
          </Card>
        </SettingsGroup>
      ) : null}

      {/* Removal is the only irreversible control on this page — it changes
          the audience of any live campaign scoped to this group — so it
          confirms first, mirroring `MembersAdmin.tsx`'s Remove confirmation. */}
      {/* ConfirmDialog (d2). Confirming hands off to the same
          fire-and-forget path the Remove button used before this dialog
          existed: the dialog closes, the row's own in-flight lock takes over,
          and a refusal surfaces wherever this mutation's error already
          renders. No pending state here — the dialog is already closed by the
          time it would matter. */}
      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={`Remove ${removeTarget?.member.displayName ?? removeTarget?.member.email ?? removeTarget?.member.userId ?? "this person"}?`}
        confirmLabel="Remove"
        onConfirm={() => {
          if (!removeTarget) return;
          removeMember.mutate({
            groupId: removeTarget.groupId,
            userId: removeTarget.member.userId,
          });
          setRemoveTarget(null);
        }}
      >
        They lose access to any campaign scoped to this group.
      </ConfirmDialog>
    </AdminSection>
  );
}
